import './SystemPasswordOnboarding.css';

import React, { useEffect } from 'react';
import { type PasswordTarget } from '@src/ConfigInfo';
import { SystemPasswordOnboardingHeader } from './SystemPasswordOnboardingHeader';
import { EnterSystemPassword } from './EnterSystemPassword';
import { CreateSystemPassword } from './CreateSystemPassword';
import { useConfigInfo } from '@src/contexts';

interface SystemPasswordOnboardingProps {
  onComplete: () => void
}

export const SystemPasswordOnboarding = ({ onComplete }: SystemPasswordOnboardingProps): JSX.Element => {
  const { configInfo } = useConfigInfo();
  const passwordTargets = configInfo !== undefined ? configInfo.passwordTargets : [];

  useEffect(() => {
    if (allPasswordsHaveBeenSet(passwordTargets)) {
      onComplete();
    }
  }, []);

  const hasCustomPassword = isCustomPasswordSet(passwordTargets);
  const systemPasswordComponent = hasCustomPassword
    ? <EnterSystemPassword onPasswordEntered={onComplete} passwordTargets={passwordTargets} onSkipPassword={onComplete} />
    : <CreateSystemPassword onPasswordCreated={onComplete} passwordTargets={passwordTargets} onSkipPassword={onComplete} />;

  return (
    <div id="system_password_onboarding">
      <div id="system_password_onboarding_header_container">
        <SystemPasswordOnboardingHeader title={hasCustomPassword ? 'Enter System Password' : 'Welcome to Vention'} />
      </div>
      <div id="system_password_onboarding_content_container">
        {systemPasswordComponent}
      </div>
    </div>
  );
};

/**
 * Checks all the password targets to determine if all of them have been changed from their default.
 */
function allPasswordsHaveBeenSet (passwordTargets: PasswordTarget[]): boolean {
  return passwordTargets
    .map(({ defaultTargets = {} }) => Object.values(defaultTargets))
    .flat()
    .filter((isDefault): isDefault is boolean => typeof isDefault === 'boolean')
    .every(isDefault => !isDefault);
}

/**
 * Checks all the password targets to determine if any of them is custom (not default).
 * This would mean that the system password has been set for at least one machine, but not all of them.
 */
export function isCustomPasswordSet (passwordTargets: PasswordTarget[]): boolean {
  return passwordTargets
    .map(({ defaultTargets = {} }) => Object.values(defaultTargets))
    .flat()
    .filter((isDefault): isDefault is boolean => typeof isDefault === 'boolean')
    .some(isDefault => !isDefault);
}
